import { useEffect, useState } from 'react';
import { get, ref, update } from 'firebase/database';
import { db } from '../../../config/firebase';
import NumPad from './NumPad';
import { useAcademySettings } from '../../../hooks/useAcademySettings';

const PIN_LENGTH = 4;

const KioskLock = ({ operator, onUnlock }) => {
  const { academy } = useAcademySettings();
  const [mode, setMode] = useState('loading');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [stage, setStage] = useState('enter');
  const [error, setError] = useState('');
  const [storedPin, setStoredPin] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!operator?.uid) {
        setMode('setup');
        return;
      }
      try {
        const snapshot = await get(ref(db, `users/${operator.uid}/kioskPin`));
        const value = snapshot.val();
        if (cancelled) return;
        if (value && typeof value === 'string' && value.length === PIN_LENGTH) {
          setStoredPin(value);
          setMode('unlock');
        } else {
          setMode('setup');
        }
      } catch (err) {
        console.error('Failed to read kiosk PIN:', err);
        if (!cancelled) setMode('setup');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [operator?.uid]);

  const reset = () => {
    setPin('');
    setConfirmPin('');
    setError('');
    setStage('enter');
  };

  const handleUnlock = () => {
    if (pin.length !== PIN_LENGTH) return;
    if (pin === storedPin) {
      reset();
      onUnlock?.();
    } else {
      setError('Wrong PIN. Try again.');
      setPin('');
    }
  };

  const handleSetup = async () => {
    if (stage === 'enter') {
      if (pin.length !== PIN_LENGTH) return;
      setStage('confirm');
      return;
    }

    if (confirmPin.length !== PIN_LENGTH) return;
    if (pin !== confirmPin) {
      setError('PINs do not match. Start again.');
      reset();
      return;
    }

    try {
      if (operator?.uid) {
        await update(ref(db, `users/${operator.uid}`), {
          kioskPin: pin,
          kioskPinUpdatedAt: new Date().toISOString(),
        });
      }
      setStoredPin(pin);
      setMode('unlock');
      reset();
      onUnlock?.();
    } catch (err) {
      console.error('Failed to save kiosk PIN:', err);
      setError('Could not save PIN. Try again.');
    }
  };

  const activeValue = mode === 'setup' && stage === 'confirm' ? confirmPin : pin;
  const setActiveValue = mode === 'setup' && stage === 'confirm' ? setConfirmPin : setPin;

  useEffect(() => {
    if (mode !== 'unlock') return;
    if (pin.length === PIN_LENGTH) {
      handleUnlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, mode]);

  useEffect(() => {
    if (mode === 'loading') return undefined;

    const handleKey = (event) => {
      if (event.defaultPrevented) return;
      const target = event.target;
      const tag = target?.tagName;
      const isEditable =
        target?.isContentEditable ||
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT';
      if (isEditable) return;

      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        setError('');
        setActiveValue((activeValue + event.key).slice(0, PIN_LENGTH));
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        setError('');
        setActiveValue(activeValue.slice(0, -1));
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (mode === 'unlock') {
          handleUnlock();
        } else {
          handleSetup();
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        setActiveValue('');
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, stage, activeValue, pin, confirmPin, storedPin]);

  const title =
    mode === 'unlock'
      ? 'Kiosk locked'
      : stage === 'enter'
        ? 'Set a kiosk PIN'
        : 'Confirm kiosk PIN';

  const description =
    mode === 'unlock'
      ? 'Enter the 4-digit PIN to resume.'
      : stage === 'enter'
        ? 'Choose a 4-digit PIN you will remember. It unlocks the tablet without signing you out.'
        : 'Enter the same 4 digits once more.';

  if (mode === 'loading') {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-950/80 text-white">
        Loading kiosk...
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-950/90 p-4 pb-[env(safe-area-inset-bottom)] text-white">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-[32px] bg-stone-900 p-6 shadow-2xl">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">
            {(academy?.shortName || academy?.name || 'Brissbella')} Kiosk
          </p>
          <h2 className="mt-2 text-2xl font-semibold">{title}</h2>
          <p className="mt-2 text-sm text-white/70">{description}</p>
        </div>

        <div className="flex justify-center gap-3">
          {Array.from({ length: PIN_LENGTH }).map((_, index) => (
            <span
              key={index}
              className={`h-4 w-4 rounded-full border border-white/40 transition ${
                index < activeValue.length ? 'bg-white' : 'bg-transparent'
              }`}
            />
          ))}
        </div>

        {error ? (
          <p className="text-center text-sm text-red-400">{error}</p>
        ) : null}

        <NumPad
          value={activeValue}
          onChange={(next) => {
            setError('');
            setActiveValue(next.slice(0, PIN_LENGTH));
          }}
          maxLength={PIN_LENGTH}
          allowClear
        />

        {mode === 'setup' ? (
          <button
            type="button"
            onClick={handleSetup}
            disabled={activeValue.length !== PIN_LENGTH}
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {stage === 'enter' ? 'Continue' : 'Save PIN'}
          </button>
        ) : null}

        {mode === 'unlock' ? (
          <button
            type="button"
            onClick={() => {
              setMode('setup');
              reset();
              setStoredPin('');
            }}
            className="text-center text-xs font-medium uppercase tracking-wide text-white/60 hover:text-white"
          >
            Forgot? Set a new PIN
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default KioskLock;
